import {clinchEnums} from '@cl/shared';
import getProductStrategyRelatedLogics from 'common/src/modules/campaigns/utils/getProductStrategyRelatedLogics';
import {
  DTProductSelectionNode,
  ProductMatchingOperator,
  ProductMatchingOperatorDescription,
  ProductSelectionNodeOutputData,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {CLEnumKeys, CLEnumType} from 'common/src/modules/enums/enums.types';
import {ProductType, productTypeUINamesByValue} from 'common/src/modules/products/products.enums';
import {ProductSelectionStrategyType} from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.types';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {toAiProductStrategy} from '../aiModels/cardDetailsAI.enums';
import {OutputType} from 'common/src/modules/nodes/nodes.types';
import {
  formatOutputHeader,
  getBaseHeader,
  getCategoryUINamePart,
  getDestinationPhrase,
  getOutputTypePhrase,
  sortOutputsByType,
} from './utils';

function getProductPurposeLine(
  card: DTProductSelectionNode,
  strategyUiName: string | null | undefined,
): string {
  const hasMatchingConditions =
    card.configuration?.conditions?.length &&
    card.productsSelectionStrategy === ProductSelectionStrategyType.ProductMatching;

  if (hasMatchingConditions && card.configuration?.conditions?.length) {
    const signalNames = card.configuration.conditions
      .map(
        (condition) =>
          `${getCategoryUINamePart(condition.signalMainCategory)}${condition.feedColumnUIName}`,
      )
      .join(' and ');

    return `Purpose: Match products by ${signalNames}`;
  }

  const strategyName = strategyUiName ?? toAiProductStrategy(card.productsSelectionStrategy);

  return strategyName ? `Purpose: ${strategyName}` : '';
}

function getProductOutputWhenPhrase(output: ProductSelectionNodeOutputData): string {
  switch (output.type) {
    case OutputType.Regular:
    case OutputType.Last:
      return 'when enough items for creatives ahead';
    case OutputType.PartialSuccess:
      return 'when some items but not enough for creatives ahead';
    case OutputType.Fallback:
    default:
      return 'when no items';
  }
}

export function productCardToText(
  card: DTProductSelectionNode,
  mode: 'short' | 'full',
  fallbackCreativeNodeId?: number,
): string {
  const header = getBaseHeader(card);
  const feed = card.feed;
  const productTypeName =
    feed?.productType != null ? productTypeUINamesByValue[feed.productType as ProductType] : '';

  const campaignEnumMapper = clinchEnums.loadedEnumsSubject.data[CLEnumType.CampaignPage];
  const strategyUiName =
    campaignEnumMapper?.[CLEnumKeys.CampaignPage.CampaignContentType]?.byValue[
      card.productsSelectionStrategy
    ]?.uiName ??
    toAiProductStrategy(card.productsSelectionStrategy) ??
    '';
  const sortedOutputs = sortOutputsByType(card.outputs ?? []);

  if (mode === 'short') {
    const strategyLine = `Selection Strategy: ${strategyUiName}`;
    const purposeLine = getProductPurposeLine(card, strategyUiName);

    const outputLines = sortedOutputs.map((output: ProductSelectionNodeOutputData, index) => {
      const outputHeader = formatOutputHeader(index, output);
      const outputType = getOutputTypePhrase(output);
      const dest = getDestinationPhrase(output.nodeId, fallbackCreativeNodeId, output.type);

      return `${outputHeader} ${outputType} ${dest}`;
    });

    const lines = [header, strategyLine, purposeLine, ...outputLines].filter(Boolean);

    return lines.join('\n');
  }

  const purposeLine = getProductPurposeLine(card, strategyUiName);

  const {isTopItemsOriented, isCategoryTopItemsOriented, isRetargetingOriented} =
    campaignEnumMapper && card.productsSelectionStrategy
      ? getProductStrategyRelatedLogics(campaignEnumMapper, card.productsSelectionStrategy)
      : {
          isTopItemsOriented: true,
          isCategoryTopItemsOriented: true,
          isRetargetingOriented: true,
        };

  const productFeedLine = feed
    ? `${productTypeName} Product Feed '${feed.name}' ID ${feed.id}`
    : 'Product Feed not set';
  const productSetLine = isRetargetingOriented
    ? card.retargetingProductSetLogic
      ? `Product Set '${card.retargetingProductSetLogic.name}' ID ${card.retargetingProductSetLogic.id}`
      : 'Product Set not set'
    : null;
  const topItemsLine = isTopItemsOriented
    ? card.topItemLogic
      ? `Top Items '${card.topItemLogic.name}' ID ${card.topItemLogic.id}`
      : 'Top Items not set'
    : null;
  const categoryTopItemsLine = isCategoryTopItemsOriented
    ? card.categoryTopItemsLogic
      ? `Category Top Items '${card.categoryTopItemsLogic.name}' ID ${card.categoryTopItemsLogic.id}`
      : 'Category Top Items not set'
    : null;
  const isMatchingStrategy =
    card.productsSelectionStrategy === ProductSelectionStrategyType.ProductMatching ||
    card.productsSelectionStrategy === ProductSelectionStrategyType.ProductMatchingVideo;
  const isSpecificProducts =
    card.productsSelectionStrategy === ProductSelectionStrategyType.SpecificProducts;

  const specificProductIdsLine =
    isSpecificProducts && card.productIds?.length
      ? `Specific Product Ids [${card.productIds.map((id) => `'${id}'`).join(',')}]`
      : isSpecificProducts
        ? 'Specific Product Ids not set'
        : null;

  const matchingStrategyConditionsLines =
    isMatchingStrategy && card.configuration?.conditions?.length
      ? card.configuration.conditions.map((c) => {
          return `Product ${c.feedColumnUIName} (Signal Key: ${c.feedColumnSignalKey}) ${getMatchingEnumValue(ProductMatchingOperator, ProductMatchingOperatorDescription, c.operator)} ${getCategoryUINamePart(c.signalMainCategory)}${c.signalUIName} (Signal Key: ${c.signalKey})`;
        })
      : [];
  const matchingStrategyLines =
    isMatchingStrategy && card.configuration?.conditions?.length
      ? ['Matching Strategy Conditions:', ...matchingStrategyConditionsLines]
      : isMatchingStrategy
        ? ['Matching Strategy Conditions: not set']
        : [];

  const outputLines = sortedOutputs.map((output: ProductSelectionNodeOutputData, index) => {
    const outputHeader = formatOutputHeader(index, output);
    const dest = getDestinationPhrase(output.nodeId, fallbackCreativeNodeId, output.type);
    const whenPhrase = getProductOutputWhenPhrase(output);

    return `${outputHeader} ${dest} ${whenPhrase}`;
  });

  const lines = [
    header,
    purposeLine,
    productFeedLine,
    productSetLine,
    topItemsLine,
    categoryTopItemsLine,
    specificProductIdsLine,
    ...matchingStrategyLines,
    ...outputLines,
  ].filter(Boolean);

  return lines.join('\n');
}
